import { IFormulaFunction } from "./interfaces/formulaInterfaces";

export const formulaFunctions: IFormulaFunction[] = [
  // MATH functions----------------------------------------------------------------
  {
    name: 'abs',
    type: 'math',
    description: 'Returns the absolute value of a number. The absolute value of a number is the number without its sign.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The real number of which you want the absolute value.',
        placeholder: 'number',
      },
    ]
  },
  {
    name: 'count',
    type: 'math',
    description: 'Counts number of fields, or number of repeatable instances',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The fields names to be counted',
        placeholder: 'number',
      },
    ]
  },
  {
    name: 'average',
    type: 'math',
    description: 'Returns the average (arithmetic mean) of the arguments. For example, if the range A1:A20 contains numbers, the formula =AVERAGE(A1:A20) returns the average of those numbers.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The first field you want the average.',
        placeholder: 'number1'
      },
      {
        type: 'number',
        required: false,
        repeatable: true,
        description: 'Additional fields for which you want the average, up to a maximum of 255.',
        placeholder: 'number2'
      }
    ]
  },
  {
    name: 'delta',
    type: 'math',
    description: 'Tests whether two fields are equal. Returns 1 if number1 = number2; returns 0 otherwise. Use this function to filter a set of values.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The first number.',
        placeholder: 'number1'
      },
      {
        type: 'number',
        required: false,
        description: 'The second number. If omitted, number2 is assumed to be zero.',
        placeholder: 'number2'
      }
    ]
  },
  {
    name: 'fact',
    type: 'math',
    description: 'Returns the factorial of a number. The factorial of a number is equal to 1*2*3*...* number.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The nonnegative number for which you want the factorial. If number is not an integer, it is truncated.',
        placeholder: 'number'
      }
    ]
  },
  {
    name: 'max',
    type: 'math',
    description: 'Returns the largest value in a set of values.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'Number1 is required, subsequent fields are optional. 1 to 255 fields for which you want to find the maximum value.',
        placeholder: 'number1'
      },
      {
        type: 'number',
        required: false,
        repeatable: true,
        description: 'Number1 is required, subsequent fields are optional. 1 to 255 fields for which you want to find the maximum value.',
        placeholder: 'number2'
      }
    ]
  },
  {
    name: 'median',
    type: 'math',
    description: 'Returns the median of the given numbers. The median is the number in the middle of a set of numbers.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'Number1 is required, subsequent fields are optional. 1 to 255 fields for which you want the median.',
        placeholder: 'number1'
      },
      {
        type: 'number',
        required: false,
        repeatable: true,
        description: 'Number1 is required, subsequent fields are optional. 1 to 255 fields for which you want the median.',
        placeholder: 'number2'
      }
    ]
  },
  {
    name: 'min',
    type: 'math',
    description: 'Returns the median of the given numbers. The median is the number in the middle of a set of numbers.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'Number1 is required, subsequent fields are optional. 1 to 255 fields for which you want the median.',
        placeholder: 'number1'
      },
      {
        type: 'number',
        required: false,
        repeatable: true,
        description: 'Number1 is required, subsequent fields are optional. 1 to 255 fields for which you want the median.',
        placeholder: 'number2'
      }
    ]
  },
  {
    name: 'mode',
    type: 'math',
    description: 'MODE returns the most frequently occurring, or repetitive, value in an array or range of data.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The first number argument for which you want to calculate the mode.',
        placeholder: 'number1'
      },
      {
        type: 'number',
        required: false,
        repeatable: true,
        description: 'Fields 2 to 255 for which you want to calculate the mode.',
        placeholder: 'number2'
      }
    ]
  },
  {
    name: 'sum',
    type: 'math',
    description: 'Sum adds values. You can add individual values, or sum the instances of a repeatable.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The first value to sum, or the repeatable to sum',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: false,
        description: 'The second value to sum',
        placeholder: 'number'
      }
    ]
  },
  {
    name: 'product',
    type: 'math',
    description: 'Product multiplies values. You can multiply individual values, or multiply the instances of a repeatable.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The first value to multiple, or the repeatable to multiply',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: false,
        description: 'The second value to sum',
        placeholder: 'number'
      }
    ]
  },
  {
    name: 'mround',
    type: 'math',
    description: 'MROUND returns a number rounded to the desired multiple.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The value to round.',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: true,
        description: 'The multiple to which you want to round number.',
        placeholder: 'multiple'
      }
    ]
  },
  {
    name: 'round',
    type: 'math',
    description: 'The ROUND function rounds a number to a specified number of digits. For example, if the field contains 23.7825, and you want to round that value to two decimal places, you can use the following formula: =ROUND(A1, 2). The result is 23.78.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The number that you want to round.',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: true,
        description: 'The number of digits to which you want to round the number argument.',
        placeholder: 'num_digits'
      }
    ]
  },
  {
    name: 'roundup',
    type: 'math',
    description: 'Rounds a number up, away from 0 (zero).',
    args: [
      {
        type: 'number',
        required: true,
        description: 'Any real number that you want rounded up.',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: true,
        description: 'The number of digits to which you want to round number.',
        placeholder: 'num_digits'
      }
    ]
  },
  {
    name: 'rounddown',
    type: 'math',
    description: 'Rounds a number down, toward zero.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'Any real number that you want rounded down.',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: true,
        description: 'The number of digits to which you want to round number.',
        placeholder: 'num_digits'
      }
    ]
  },
  {
    name: 'sqrt',
    type: 'math',
    description: 'Returns a positive square root.',
    args: [
      {
        type: 'number',
        required: true,
        description: 'The number for which you want the square root.',
        placeholder: 'number'
      }
    ]
  },
  {
    name: 'counta',
    type: 'math',
    description: 'Returns the number of values in a dataset.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'value1'
      },
      {
        type: 'number',
        required: false,
        description: '',
        placeholder: 'value2'
      }
    ]
  },
  {
    name: 'countif',
    type: 'math',
    description: 'Returns a conditional count across a range.',
    args: [
      {
        type: 'string',
        required: true,
        repeatable: true,
        description: '',
        placeholder: 'range'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'criterion'
      }
    ]
  },
  {
    name: 'countblank',
    type: 'math',
    description: 'Returns a conditional count across a range.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value1'
      },
      {
        type: 'string',
        required: false,
        description: '',
        repeatable: true,
        placeholder: 'value2'
      }
    ]
  },

  // String functions----------------------------------------------------------------
  {
    name: 'concatenate',
    type: 'string',
    description: 'Use CONCATENATE to join two or more text strings into one string.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text1'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'text2'
      }
    ]
  },
  {
    name: 'exact',
    type: 'string',
    description: 'Compares two text strings and returns TRUE if they are exactly the same, FALSE otherwise. EXACT is case-sensitive but ignores formatting differences. Use EXACT to test text being entered into a document.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text1'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text2'
      }
    ]
  },
  {
    name: 'left',
    type: 'string',
    description: 'LEFT returns the first character or characters in a text string, based on the number of characters you specify.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text1'
      },
      {
        type: 'number',
        required: false,
        description: '',
        placeholder: 'num_chars'
      }
    ]
  },
  {
    name: 'len',
    type: 'string',
    description: 'LEN returns the number of characters in a text string.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text'
      }
    ]
  },
  {
    name: 'lower',
    type: 'string',
    description: 'Converts all uppercase letters in a text string to lowercase.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text'
      }
    ]
  },
  {
    name: 'right',
    type: 'string',
    description: 'RIGHT returns the last character or characters in a text string, based on the number of characters you specify.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text'
      },
      {
        type: 'number',
        required: false,
        description: '',
        placeholder: 'num_chars'
      }
    ]
  },
  {
    name: 'trim',
    type: 'string',
    description: 'Removes all spaces from text except for single spaces between words. Use TRIM on text that you have received from another application that may have irregular spacing.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text'
      }
    ]
  },
  {
    name: 'upper',
    type: 'string',
    description: 'Converts text to uppercase.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'text'
      }
    ]
  },
  {
    name: 'join',
    type: 'string',
    description: 'Use JOIN to concatenate two or more elements using a delimiter.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'delimiter'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'element'
      },
    ]
  },
  {
    name: 'dollar',
    type: 'string',
    description: 'Converts a number to text in currency format.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'number'
      },
      {
        type: 'number',
        required: false,
        description: '',
        placeholder: 'decimals'
      },
    ]
  },

  // DATE/TIME functions----------------------------------------------------------------
  {
    name: 'date',
    type: 'datetime',
    description: 'The DATE function returns the sequential serial number that represents a particular date.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'year'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'month'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'day'
      },
    ]
  },
  {
    name: 'datevalue',
    type: 'datetime',
    description: 'The DATEVALUE function converts dates that are stored as text into a standard format.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'date_text'
      }
    ]
  },
  {
    name: 'day',
    type: 'datetime',
    description: 'Returns the day of a date, represented by a serial number. The day is given as an integer ranging from 1 to 31.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      }
    ]
  },
  {
    name: 'days',
    type: 'datetime',
    description: 'Returns the number of days between two dates.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'end_date'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'start_date'
      }
    ]
  },
  {
    name: 'edate',
    type: 'datetime',
    description: 'Returns the serial number that represents the date that is the indicated number of months before or after a specified date (the start_date). Use EDATE to calculate maturity dates or due dates that fall on the same day of the month as the date of issue.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'start_date'
      },
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'months'
      }
    ]
  },
  {
    name: 'eomonth',
    type: 'datetime',
    description: 'Returns the serial number for the last day of the month that is the indicated number of months before or after start_date. Use EOMONTH to calculate maturity dates or due dates that fall on the last day of the month.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'start_date'
      },
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'months'
      }
    ]
  },
  {
    name: 'hour',
    type: 'datetime',
    description: 'Returns the hour of a time value. The hour is given as an integer, ranging from 0 (12:00 A.M.) to 23 (11:00 P.M.).',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      }
    ]
  },
  {
    name: 'minute',
    type: 'datetime',
    description: 'Returns the minutes of a time value. The minute is given as an integer, ranging from 0 to 59.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      }
    ]
  },
  {
    name: 'month',
    type: 'datetime',
    description: 'Returns the month of a date represented by a serial number. The month is given as an integer, ranging from 1 (January) to 12 (December).',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      }
    ]
  },
  {
    name: 'now',
    type: 'datetime',
    description: 'The NOW function is useful when you need to display the current date and time on a worksheet or calculate a value based on the current date and time, and have that value updated each time you open the worksheet.',
    args: []
  },
  {
    name: 'second',
    type: 'datetime',
    description: 'Returns the seconds of a time value. The second is given as an integer in the range 0 (zero) to 59.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      }
    ]
  },
  {
    name: 'time',
    type: 'datetime',
    description: 'Returns the decimal number for a particular time. If the cell format was General before the function was entered, the result is formatted as a date. The decimal number returned by TIME is a value ranging from 0 (zero) to 0.99988426, representing the times from 0:00:00 (12:00:00 AM) to 23:59:59 (11:59:59 P.M.).',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'hour'
      },
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'minute'
      },
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'second'
      }
    ]
  },
  {
    name: 'today',
    type: 'datetime',
    description: "Returns the serial number of the current date. The serial number is the date-time code used by Excel for date and time calculations. If the cell format was General before the function was entered, Excel changes the cell format to Date. If you want to view the serial number, you must change the cell format to Generalor Number. The TODAY function is useful when you need to have the current date displayed on a worksheet, regardless of when you open the workbook. It is also useful for calculating intervals. For example, if you know that someone was born in 1963, you might use the following formula to find that person's age as of this year's birthday: = YEAR( TODAY())-1963",
    args: []
  },
  {
    name: 'weekday',
    type: 'datetime',
    description: 'Returns the day of the week corresponding to a date. The day is given as an integer, ranging from 1 (Sunday) to 7 (Saturday), by default.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      },
      {
        type: 'string',
        required: false,
        description: '',
        placeholder: 'return_type'
      }
    ]
  },
  {
    name: 'weeknum',
    type: 'datetime',
    description: 'Returns the week number of a specific date. For example, the week containing January 1 is the first week of the year, and is numbered week 1.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      },
      {
        type: 'number',
        required: false,
        description: '',
        placeholder: 'return_type'
      }
    ]
  },
  {
    name: 'workday',
    type: 'datetime',
    description: 'Returns a number that represents a date that is the indicated number of working days before or after a date (the starting date). Working days exclude weekends and any dates identified as holidays. Use WORKDAY to exclude weekends or holidays when you calculate invoice due dates, expected delivery times, or the number of days of work performed.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'start_date'
      },
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'days'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'holidays'
      }
    ]
  },
  {
    name: 'year',
    type: 'datetime',
    description: 'Returns the year corresponding to a date. The year is returned as an integer in the range 1900-9999.',
    args: [
      {
        type: 'number',
        required: true,
        description: '',
        placeholder: 'serial_number'
      }
    ]
  },
  {
    name: 'formatdate',
    type: 'datetime',
    description: 'Returns a date formatted as a string.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'date',
      },
      {
        type: 'string',
        required: true,
        description: 'Use a date format pattern like "MM/DD/YYYY" or "MM/DD/YYYY hh:mm:ss"',
        placeholder: 'format',
      },
      {
        type: 'string',
        required: false,
        description: 'Use a time zone identifier like "America/New_York" or "UTC". If omitted, "America/New_York" is used.',
        placeholder: 'timezone',
      }
    ]
  },

  // LOGICAL functions----------------------------------------------------------------
  {
    name: 'and',
    type: 'logical',
    description: 'Use the AND function, to determine if all conditions in a test are TRUE.\n',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'logical1'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'logical2'
      }
    ]
  },
  {
    name: 'if',
    type: 'logical',
    description: "Use the IF function, one of the logical functions, to return one value if a condition is true and another value if it's false.",
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'logical_test'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value_if_true'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'value_if_false'
      }
    ]
  },
  {
    name: 'not',
    type: 'logical',
    description: 'The NOT function reverses the value of its argument.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'logical'
      }
    ]
  },
  {
    name: 'or',
    type: 'logical',
    description: 'The OR function returns TRUE if any of its arguments evaluate to TRUE, and returns FALSE if all of its arguments evaluate to FALSE.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'logical1'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'logical2'
      }
    ]
  },
  {
    name: 'switch',
    type: 'logical',
    description: 'The SWITCH function evaluates one value (called the expression) against a list of values, and returns the result corresponding to the first matching value. If there is no match, an optional default value may be returned.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'expression'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value1'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'result1'
      }
    ]
  },
  {
    name: 'xor',
    type: 'logical',
    description: 'The XOR function returns a logical Exclusive Or of all arguments.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'logical1'
      },
      {
        type: 'string',
        required: false,
        repeatable: true,
        description: '',
        placeholder: 'logical2'
      }
    ]
  },
  {
    name: 'ifs',
    type: 'logical',
    description: 'Evaluates multiple conditions and returns a value that corresponds to the first true condition.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'condition1'
      },
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value1'
      },
      {
        type: 'string',
        required: false,
        description: '',
        placeholder: 'condition2'
      },
      {
        type: 'string',
        required: false,
        description: '',
        placeholder: 'value2'
      }
    ]
  },
  {
    name: 'isblank',
    type: 'logical',
    description: 'Checks whether the referenced field is empty.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value1'
      }
    ]
  },
  {
    name: 'isempty',
    type: 'logical',
    description: 'Checks whether the referenced field or list is empty.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value1'
      }
    ]
  },
  {
    name: 'iferror',
    type: 'logical',
    description: 'Returns the first argument if it is not an error value, otherwise returns the second argument if present, or a blank if the second argument is absent.',
    args: [
      {
        type: 'string',
        required: true,
        description: '',
        placeholder: 'value',
      },
      {
        type: 'string',
        required: false,
        description: '',
        placeholder: 'value_if_error',
      },
    ],
  },
];